// LocalStorage fallback for the 2FA verification token.
//
// Backend's POST /phone_verifications returns `verification_session_already_active`
// without a token when an open TFA session already exists for this (patient,
// distribution). Without that token the OTP form has nothing to send and silently
// no-ops. We persist the token from the original `verification_required` /
// resend / `createVerificationSession` response and replay it here, so the user
// can finish the flow with the SMS code they already received.
//
// Scope: localStorage, keyed on slug + dial code + phone. Persists across tabs
// and reloads — sessionStorage is too narrow because the backend session lasts
// longer than a single tab and the SMS may be opened/clicked in a different tab.
// Cleared on successful authentication (token consumed) and on "Zmień numer".

const KEY_PREFIX = "vd:verification_token";

const buildKey = (slug: string, dialCode: string, phone: string) => `${KEY_PREFIX}:${slug}:${dialCode}${phone}`;

const safeStorage = (): Storage | null => {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

export const readStoredVerificationToken = (slug: string, dialCode: string, phone: string): string | null => {
  const storage = safeStorage();
  if (!storage) return null;
  try {
    return storage.getItem(buildKey(slug, dialCode, phone));
  } catch {
    return null;
  }
};

export const writeStoredVerificationToken = (slug: string, dialCode: string, phone: string, token: string): void => {
  const storage = safeStorage();
  if (!storage) return;
  try {
    storage.setItem(buildKey(slug, dialCode, phone), token);
  } catch {
    // Quota or privacy mode — silently ignore; the user will hit the dead-end branch
    // if they later land on `verification_session_already_active` without a token.
  }
};

export const clearStoredVerificationToken = (slug: string, dialCode: string, phone: string): void => {
  const storage = safeStorage();
  if (!storage) return;
  try {
    storage.removeItem(buildKey(slug, dialCode, phone));
  } catch {
    // ignore
  }
};
